import { BADGE_DEFINITIONS, calculateLevel, calculateXP, xpProgressInLevel, } from '../models/gamification';
import { dateKey, habitRecord, habitScheduleOf, habitStartKey, historicalSubject, parseDateKey, shiftDateKey, } from './habit-schedule';
import { currentStreakFromDates, longestStreakFromDates } from './undo';
import { wallClockIn } from '../utils/date-utils';
/** Derive the facts from history. Pure; O(tasks + instances + habit-days). */
export function activityFacts({ tasks, habitInstances, today, timezone }) {
    const habits = tasks.filter((t) => t.is_habit);
    const habitById = new Map(habits.map((h) => [h.id, h]));
    let completions = 0;
    let xp = 0;
    let earlyBird = false;
    let nightOwl = false;
    let timeBoxer = false;
    const days = new Set();
    for (const task of tasks) {
        if (task.is_habit || task.status !== 'completed')
            continue;
        completions += 1;
        xp += task.xp_awarded ?? calculateXP(task.difficulty, task.estimated_minutes, false);
        const moment = completedMoment(task.completed_at, timezone);
        if (moment) {
            days.add(moment.day);
            if (moment.hour < 9)
                earlyBird = true;
            if (moment.hour >= 21)
                nightOwl = true;
        }
        if ((task.actual_minutes ?? 0) > 0 && task.estimated_minutes > 0
            && task.actual_minutes <= task.estimated_minutes) {
            timeBoxer = true;
        }
    }
    const byHabit = new Map();
    const doneByHabit = new Map();
    for (const instance of habitInstances) {
        completions += 1;
        days.add(instance.date);
        const habit = habitById.get(instance.habit_id);
        // An instance whose habit is gone still counts as a completion and a day
        // worked; it can't be priced without the habit, so it adds no XP here —
        // the stored total kept what it paid.
        xp += instance.xp_awarded ?? (habit ? calculateXP(habit.difficulty, habit.estimated_minutes, true) : 0);
        let own = byHabit.get(instance.habit_id);
        if (!own) {
            own = [];
            byHabit.set(instance.habit_id, own);
            doneByHabit.set(instance.habit_id, new Set());
        }
        own.push(instance);
        doneByHabit.get(instance.habit_id).add(instance.date);
    }
    // Per-habit best runs: habitRecord is the habit page's own reading. Each is
    // handed only its own instances — the same answer, without every habit
    // scanning everyone's.
    let bestHabitStreak = 0;
    for (const habit of habits) {
        const own = byHabit.get(habit.id);
        if (!own)
            continue;
        const { longestStreak } = habitRecord(habit, own, today, today, today, habit.id);
        if (longestStreak > bestHabitStreak)
            bestHabitStreak = longestStreak;
    }
    const perfect = perfectDayRuns(habits, doneByHabit, today);
    const activeDays = [...days];
    // Anchored at the later of today and the latest active day: a completion
    // stamped "tomorrow" by a clock or zone that disagrees with `today` still
    // continues the streak rather than reading as nothing done.
    const latest = activeDays.reduce((max, d) => (d > max ? d : max), '');
    const asOf = latest > today ? latest : today;
    return {
        completions,
        habitCompletions: habitInstances.length,
        xp,
        asOf,
        currentStreak: currentStreakFromDates(activeDays, asOf),
        longestStreak: longestStreakFromDates(activeDays),
        bestHabitStreak,
        ...perfect,
        earlyBird,
        nightOwl,
        timeBoxer,
    };
}
/**
 * The local day and hour a completion happened — the day `completedOnKey`
 * gives, and the hour on the same wall clock.
 *
 * Remembered per (zone, timestamp). Reading an instant in a named zone goes
 * through Intl, a few microseconds each; a long history is thousands of
 * completions, and they're re-read on every completion. A finished task's
 * timestamp doesn't change, so after the first pass this is a lookup. Without
 * a zone it's the host clock, which is cheap and not worth caching (and the
 * host zone can change under a cache).
 */
const moments = new Map();
function completedMoment(completedAt, timezone) {
    if (!completedAt)
        return null;
    const key = timezone ? `${timezone}|${completedAt}` : null;
    if (key) {
        const known = moments.get(key);
        if (known !== undefined)
            return known;
    }
    const at = new Date(completedAt);
    let moment = null;
    if (!Number.isNaN(at.getTime())) {
        const wall = wallClockIn(timezone, at);
        moment = { day: dateKey(wall), hour: wall.getHours() };
    }
    if (key) {
        // A ceiling, so a session can't grow it without bound.
        if (moments.size > 200_000)
            moments.clear();
        moments.set(key, moment);
    }
    return moment;
}
/**
 * Perfect days: at least one habit due, and every habit due was done. A day
 * with nothing due neither makes nor breaks a run; today counts once it's
 * complete and never breaks one while it's still open.
 */
function perfectDayRuns(habits, doneByHabit, today) {
    const live = habits.map((h) => {
        const { subject, end } = historicalSubject(h);
        return {
            scheduled: habitScheduleOf(subject), end, start: habitStartKey(subject), done: doneByHabit.get(h.id),
        };
    });
    const starts = live.map((h) => h.start).filter((s) => !!s).sort();
    const out = { perfectDays: 0, currentPerfectStreak: 0, bestPerfectStreak: 0, lastPerfectDay: null };
    const day = starts.length > 0 ? parseDateKey(starts[0]) : null;
    if (!day)
        return out;
    let run = 0;
    let cursor = starts[0];
    // Twenty years of days: far past any real history, short of a runaway loop.
    for (let guard = 0; guard < 7300 && cursor <= today; guard += 1) {
        let due = 0;
        let done = 0;
        for (const h of live) {
            if (h.end && cursor > h.end)
                continue;
            if (!h.scheduled(cursor, day))
                continue;
            due += 1;
            if (h.done?.has(cursor))
                done += 1;
        }
        if (due > 0) {
            if (done === due) {
                out.perfectDays += 1;
                out.lastPerfectDay = cursor;
                run += 1;
                if (run > out.bestPerfectStreak)
                    out.bestPerfectStreak = run;
            }
            else if (cursor !== today) {
                run = 0;
            }
        }
        day.setDate(day.getDate() + 1);
        cursor = dateKey(day);
    }
    out.currentPerfectStreak = run;
    return out;
}
export const BADGE_CATEGORY_LABELS = {
    tasks: 'Task Achievements',
    streaks: 'Activity Streaks',
    habits: 'Habit Achievements',
    perfect: 'Perfect Days',
    time: 'Time Management',
    levels: 'Level Milestones',
};
const flag = (on) => (on ? 1 : 0);
/**
 * The one rule table. Targets match the descriptions in BADGE_DEFINITIONS —
 * the habit badges are 1 / 25 / 100, as they always said.
 */
export const BADGE_RULES = {
    first_task: { target: 1, category: 'tasks', measure: (s) => s.tasks_completed },
    centurion: { target: 100, category: 'tasks', measure: (s) => s.tasks_completed },
    task_master: { target: 500, category: 'tasks', measure: (s) => s.tasks_completed },
    activity_streak_3: { target: 3, category: 'streaks', measure: (s) => s.longest_streak },
    activity_streak_7: { target: 7, category: 'streaks', measure: (s) => s.longest_streak },
    activity_streak_30: { target: 30, category: 'streaks', measure: (s) => s.longest_streak },
    habit_starter: { target: 1, category: 'habits', measure: (s) => s.habits_completed },
    habit_regular: { target: 25, category: 'habits', measure: (s) => s.habits_completed },
    habit_dedicated: { target: 100, category: 'habits', measure: (s) => s.habits_completed },
    habit_streak_7: { target: 7, category: 'habits', measure: (s) => s.best_habit_streak },
    habit_streak_30: { target: 30, category: 'habits', measure: (s) => s.best_habit_streak },
    perfect_day: { target: 1, category: 'perfect', measure: (_s, f) => f.perfectDays },
    perfect_week: { target: 7, category: 'perfect', measure: (s) => s.best_perfect_day_streak },
    early_bird: { target: 1, category: 'time', measure: (_s, f) => flag(f.earlyBird) },
    night_owl: { target: 1, category: 'time', measure: (_s, f) => flag(f.nightOwl) },
    time_boxer: { target: 1, category: 'time', measure: (_s, f) => flag(f.timeBoxer) },
    level_5: { target: 5, category: 'levels', measure: (s) => s.level },
    level_10: { target: 10, category: 'levels', measure: (s) => s.level },
    level_25: { target: 25, category: 'levels', measure: (s) => s.level },
    level_50: { target: 50, category: 'levels', measure: (s) => s.level },
};
const BADGE_ORDER = Object.keys(BADGE_DEFINITIONS);
/** Facts for a caller that has only the stats record (no history to hand). */
export const NO_FACTS = {
    completions: 0, habitCompletions: 0, xp: 0, asOf: '', currentStreak: 0, longestStreak: 0,
    bestHabitStreak: 0, perfectDays: 0, currentPerfectStreak: 0, bestPerfectStreak: 0,
    lastPerfectDay: null, earlyBird: false, nightOwl: false, timeBoxer: false,
};
/** Every badge whose rule the numbers meet, in definition order. */
export function badgesEarnedBy(stats, facts = NO_FACTS) {
    return BADGE_ORDER.filter((type) => BADGE_RULES[type].measure(stats, facts) >= BADGE_RULES[type].target);
}
/**
 * The stats record, settled against history. The larger of stored and
 * derived for every running total and record; the current streaks from
 * history alone (they must be able to fall); badges as a union, with a
 * history entry stamped `now` for each one newly held.
 */
export function effectiveStats(stored, facts, now = new Date().toISOString()) {
    const totalXp = Math.max(stored.total_xp ?? 0, facts.xp);
    // History decides the current streak, so a stored one that went stale (no
    // completion for days, but nothing ever reset it) reads 0. While the stored
    // record's last completion is today or yesterday it still counts, so a live
    // streak survives completed tasks being deleted.
    const live = !!stored.last_completion_date && !!facts.asOf
        && stored.last_completion_date.slice(0, 10) >= shiftDateKey(facts.asOf, -1);
    const currentStreak = live
        ? Math.max(facts.currentStreak, stored.current_streak ?? 0)
        : facts.currentStreak;
    const settled = {
        ...stored,
        total_xp: totalXp,
        level: calculateLevel(totalXp),
        tasks_completed: Math.max(stored.tasks_completed ?? 0, facts.completions),
        habits_completed: Math.max(stored.habits_completed ?? 0, facts.habitCompletions),
        current_streak: currentStreak,
        // A stored current streak happened, even once it's stale — it's a record.
        longest_streak: Math.max(stored.longest_streak ?? 0, stored.current_streak ?? 0, facts.longestStreak, currentStreak),
        best_habit_streak: Math.max(stored.best_habit_streak ?? 0, facts.bestHabitStreak),
        perfect_day_streak: facts.currentPerfectStreak,
        best_perfect_day_streak: Math.max(stored.best_perfect_day_streak ?? 0, facts.bestPerfectStreak),
        last_perfect_day: maxKey(stored.last_perfect_day, facts.lastPerfectDay),
        earned_badges: stored.earned_badges ?? [],
        badge_history: stored.badge_history ?? [],
    };
    const held = new Set(settled.earned_badges);
    const fresh = badgesEarnedBy(settled, facts).filter((b) => !held.has(b));
    if (fresh.length === 0)
        return settled;
    return {
        ...settled,
        earned_badges: [...settled.earned_badges, ...fresh],
        badge_history: [...settled.badge_history, ...fresh.map((badge_type) => ({
                badge_type, earned_at: now, trigger_type: BADGE_RULES[badge_type].category,
                trigger_id: null, trigger_name: null, context: null,
            }))],
    };
}
/**
 * The stored record, brought up to what history shows — or null when it
 * already holds it.
 *
 * effectiveStats only READS the larger of the stored record and the history.
 * Writing the settled totals back matters across devices: sync merges the
 * running totals by the newer record, so after both devices worked offline
 * the stored total holds one side's work, and only the merged history holds
 * both. Persisting the settled figures makes the stored record whole again —
 * which keeps that XP even if the tasks behind it are deleted later.
 *
 * Only the fields that never fall without an undo are compared, so this
 * writes once after a merge (or a newly earned badge), not on every render;
 * current streaks are left to history.
 */
export function settledWriteBack(stored, settled) {
    const grew = (settled.total_xp ?? 0) > (stored.total_xp ?? 0)
        || (settled.tasks_completed ?? 0) > (stored.tasks_completed ?? 0)
        || (settled.habits_completed ?? 0) > (stored.habits_completed ?? 0)
        || (settled.longest_streak ?? 0) > (stored.longest_streak ?? 0)
        || (settled.best_habit_streak ?? 0) > (stored.best_habit_streak ?? 0)
        || (settled.best_perfect_day_streak ?? 0) > (stored.best_perfect_day_streak ?? 0)
        || (settled.earned_badges ?? []).length > (stored.earned_badges ?? []).length;
    if (!grew)
        return null;
    return {
        ...stored,
        total_xp: settled.total_xp,
        level: settled.level,
        tasks_completed: settled.tasks_completed,
        habits_completed: settled.habits_completed,
        longest_streak: settled.longest_streak,
        best_habit_streak: settled.best_habit_streak,
        best_perfect_day_streak: settled.best_perfect_day_streak,
        last_perfect_day: settled.last_perfect_day,
        earned_badges: settled.earned_badges,
        badge_history: settled.badge_history,
    };
}
/** Badges held after but not before, as displayable badges. */
export function newlyEarned(before, after, now = new Date().toISOString()) {
    const had = new Set(before.earned_badges ?? []);
    return (after.earned_badges ?? [])
        .filter((b) => !had.has(b))
        .map((b) => ({ ...BADGE_DEFINITIONS[b], earned_at: now }));
}
/** Every badge with where it stands — what the Badges & XP page draws. */
export function badgeProgress(stats, facts = NO_FACTS) {
    const held = new Set(stats.earned_badges ?? []);
    const earnedAt = new Map((stats.badge_history ?? []).map((a) => [a.badge_type, a.earned_at]));
    return BADGE_ORDER.map((type) => {
        const rule = BADGE_RULES[type];
        const measured = rule.measure(stats, facts);
        const earned = held.has(type) || measured >= rule.target;
        const current = earned ? rule.target : Math.min(measured, rule.target);
        return {
            badge: { ...BADGE_DEFINITIONS[type], earned_at: earnedAt.get(type) ?? null },
            category: rule.category,
            earned,
            current,
            target: rule.target,
            percent: rule.target > 0 ? Math.round((current / rule.target) * 100) : 100,
        };
    });
}
/**
 * Two devices' stats records, merged without losing either side's progress.
 *
 * Sync used to keep whichever record was newer, whole — so when both devices
 * completed work offline, one device's new badges and record streak vanished.
 *
 * - **Running totals** (XP, level, completed counts) come from the NEWER
 *   record. They move both ways — an untick takes XP back — so the larger
 *   side is not the truer one: 2.7.0 took the larger, and an untick synced
 *   against a device that still had the tick's XP was simply undone (found
 *   by e2e/two-devices-test). Work done on BOTH devices offline is not lost
 *   by this: effectiveStats settles the total against the merged history,
 *   which holds every completion, and the app writes the settled total back.
 * - **Records** (longest streaks, last days) only ever grow: the larger.
 * - **Badges** and their history: a union, earliest date kept.
 * - The current streaks come from the newer record.
 */
export function mergeUserStats(local, remote) {
    const lt = Date.parse(local.updated_at ?? '') || 0;
    const rt = Date.parse(remote.updated_at ?? '') || 0;
    const newer = rt > lt ? remote : local;
    const totalXp = newer.total_xp ?? 0;
    const badges = [...(local.earned_badges ?? [])];
    for (const b of remote.earned_badges ?? [])
        if (!badges.includes(b))
            badges.push(b);
    const history = new Map();
    for (const entry of [...(local.badge_history ?? []), ...(remote.badge_history ?? [])]) {
        const prior = history.get(entry.badge_type);
        if (!prior || entry.earned_at < prior.earned_at)
            history.set(entry.badge_type, entry);
    }
    return {
        ...newer,
        total_xp: totalXp,
        level: calculateLevel(totalXp),
        tasks_completed: newer.tasks_completed ?? 0,
        habits_completed: newer.habits_completed ?? 0,
        longest_streak: Math.max(local.longest_streak ?? 0, remote.longest_streak ?? 0),
        best_habit_streak: Math.max(local.best_habit_streak ?? 0, remote.best_habit_streak ?? 0),
        best_perfect_day_streak: Math.max(local.best_perfect_day_streak ?? 0, remote.best_perfect_day_streak ?? 0),
        last_completion_date: maxKey(local.last_completion_date, remote.last_completion_date),
        last_perfect_day: maxKey(local.last_perfect_day, remote.last_perfect_day),
        earned_badges: badges,
        badge_history: [...history.values()],
        updated_at: rt > lt ? remote.updated_at : local.updated_at,
    };
}
function maxKey(a, b) {
    if (!a)
        return b ?? null;
    if (!b)
        return a;
    return a > b ? a : b;
}
/**
 * Where a total XP sits within its level — the one formula (10·n^1.5 per
 * level). Badges & XP had its own (100·n), so its bar sat empty for nearly
 * everyone while the header's showed real progress.
 */
export function levelProgress(totalXP) {
    const [currentXP, xpNeeded] = xpProgressInLevel(totalXP);
    return {
        level: calculateLevel(totalXP),
        currentXP,
        xpNeeded,
        progressPercent: xpNeeded > 0 ? Math.min(100, (currentXP / xpNeeded) * 100) : 100,
        totalXP,
    };
}
//# sourceMappingURL=badges.js.map